import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import { useCurrency } from '../../contexts/CurrencyContext';
import {
  ArrowDownTrayIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  BanknotesIcon,
  UserIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

interface Withdrawal {
  id: number;
  reference: string;
  amount: number;
  status: string;
  bank_name: string;
  account_number: string;
  account_name: string;
  admin_note?: string;
  created_at: string;
  processed_at?: string;
  user: {
    id: number;
    uuid: string;
    email: string;
    first_name: string;
    last_name: string;
    phone: string;
  };
  wallet_balance: number;
}

const AdminWithdrawals: React.FC = () => {
  const { formatPrice } = useCurrency();
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<Withdrawal | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchWithdrawals();
  }, [filterStatus]);

  const fetchWithdrawals = async () => {
    try {
      setLoading(true);
      const data = await adminAPI.getWithdrawalRequests(
        filterStatus === 'all' ? undefined : filterStatus
      );
      setWithdrawals(data.withdrawals || []);
    } catch (err: any) {
      setError('Failed to fetch withdrawal requests');
      console.error('Error fetching withdrawals:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (withdrawalId: number) => {
    if (!window.confirm('Are you sure you want to approve this withdrawal? Funds will be transferred to the rider\'s bank account.')) {
      return;
    }

    setActionLoading(withdrawalId);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await adminAPI.approveWithdrawal(withdrawalId);
      setSuccessMessage(result.message || 'Withdrawal approved successfully');
      fetchWithdrawals();
    } catch (err: any) {
      setError(err.message || 'Failed to approve withdrawal');
    } finally {
      setActionLoading(null);
    }
  };

  const openRejectModal = (withdrawal: Withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setRejectionReason('');
    setRejectModalOpen(true);
  };

  const handleReject = async () => {
    if (!selectedWithdrawal) return;

    if (!rejectionReason.trim() || rejectionReason.trim().length < 10) {
      setError('Rejection reason must be at least 10 characters');
      return;
    }

    setActionLoading(selectedWithdrawal.id);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await adminAPI.rejectWithdrawal(selectedWithdrawal.id, rejectionReason.trim());
      setSuccessMessage(result.message || 'Withdrawal rejected successfully');
      setRejectModalOpen(false);
      setSelectedWithdrawal(null);
      setRejectionReason('');
      fetchWithdrawals();
    } catch (err: any) {
      setError(err.message || 'Failed to reject withdrawal');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { bg: string; text: string; icon: any }> = {
      pending: { bg: 'bg-amber-100', text: 'text-amber-800', icon: ClockIcon },
      processing: { bg: 'bg-blue-100', text: 'text-blue-800', icon: ClockIcon },
      completed: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircleIcon },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircleIcon },
    };

    const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', icon: ClockIcon };
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-volexx text-sm font-medium ${config.bg} ${config.text}`}>
        <Icon className="h-4 w-4" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-volexx-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-poppins">Loading withdrawal requests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 font-poppins">Withdrawal Management</h1>
          <p className="mt-2 text-gray-600">Review and process rider withdrawal requests</p>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-volexx text-green-800">
            <div className="flex items-center">
              <CheckCircleIcon className="h-5 w-5 mr-2" />
              {successMessage}
            </div>
          </div>
        )}

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-volexx text-red-800">
            <div className="flex items-center">
              <XCircleIcon className="h-5 w-5 mr-2" />
              {error}
            </div>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {['all', 'pending', 'processing', 'completed', 'rejected'].map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`${
                  filterStatus === status
                    ? 'border-volexx-blue-600 text-volexx-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm capitalize transition-colors`}
              >
                {status}
                {status === 'pending' && withdrawals.filter(w => w.status === 'pending').length > 0 && (
                  <span className="ml-2 bg-amber-100 text-amber-800 py-0.5 px-2 rounded-full text-xs font-semibold">
                    {withdrawals.filter(w => w.status === 'pending').length}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Withdrawals List */}
        {withdrawals.length === 0 ? (
          <div className="text-center py-12">
            <BanknotesIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No withdrawal requests</h3>
            <p className="mt-1 text-sm text-gray-500">
              {filterStatus === 'all' ? 'No withdrawal requests found' : `No ${filterStatus} withdrawals`}
            </p>
          </div>
        ) : (
          <div className="bg-white shadow-volexx rounded-volexx border border-gray-100 overflow-hidden">
            <div className="divide-y divide-gray-100">
              {withdrawals.map((withdrawal) => (
                <div key={withdrawal.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        {getStatusBadge(withdrawal.status)}
                        <span className="text-sm text-gray-500">Ref: {withdrawal.reference}</span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <UserIcon className="h-5 w-5 text-gray-400" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {withdrawal.user.first_name} {withdrawal.user.last_name}
                              </p>
                              <p className="text-xs text-gray-500">{withdrawal.user.email}</p>
                              <p className="text-xs text-gray-500">{withdrawal.user.phone}</p>
                            </div>
                          </div>
                          <div className="ml-7">
                            <p className="text-xs text-gray-600">
                              Wallet Balance: <span className="font-semibold">{formatPrice(withdrawal.wallet_balance)}</span>
                            </p>
                          </div>
                        </div>

                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <BanknotesIcon className="h-5 w-5 text-gray-400" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{withdrawal.bank_name}</p>
                              <p className="text-xs text-gray-500">
                                {withdrawal.account_number} - {withdrawal.account_name}
                              </p>
                            </div>
                          </div>
                          <div className="ml-7">
                            <p className="text-xs text-gray-600">
                              <CalendarIcon className="inline h-3 w-3 mr-1" />
                              {new Date(withdrawal.created_at).toLocaleString()}
                            </p>
                            {withdrawal.processed_at && (
                              <p className="text-xs text-gray-600">
                                Processed: {new Date(withdrawal.processed_at).toLocaleString()}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {withdrawal.admin_note && (
                        <div className="mt-2 p-3 bg-red-50 border border-red-100 rounded-volexx">
                          <p className="text-sm text-red-800">
                            <span className="font-semibold">Rejection Reason:</span> {withdrawal.admin_note}
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="ml-6 flex flex-col items-end gap-3">
                      <p className="text-2xl font-bold text-gray-900">{formatPrice(withdrawal.amount)}</p>
                      
                      {withdrawal.status === 'pending' && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleApprove(withdrawal.id)}
                            disabled={actionLoading === withdrawal.id}
                            className="px-4 py-2 bg-green-600 text-white rounded-volexx hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium"
                          >
                            <CheckCircleIcon className="h-4 w-4" />
                            {actionLoading === withdrawal.id ? 'Processing...' : 'Approve'}
                          </button>
                          <button
                            onClick={() => openRejectModal(withdrawal)}
                            disabled={actionLoading === withdrawal.id}
                            className="px-4 py-2 bg-red-600 text-white rounded-volexx hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium"
                          >
                            <XCircleIcon className="h-4 w-4" />
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Reject Modal */}
      {rejectModalOpen && selectedWithdrawal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-volexx max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Reject Withdrawal Request</h3>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Amount: <span className="font-semibold">{formatPrice(selectedWithdrawal.amount)}</span>
              </p>
              <p className="text-sm text-gray-600 mb-4">
                User: <span className="font-semibold">{selectedWithdrawal.user.first_name} {selectedWithdrawal.user.last_name}</span>
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rejection Reason (minimum 10 characters)
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Please provide a clear reason for rejection..."
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setRejectModalOpen(false);
                  setSelectedWithdrawal(null);
                  setRejectionReason('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-volexx hover:bg-gray-50 transition-colors"
                disabled={actionLoading === selectedWithdrawal.id}
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-volexx hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={actionLoading === selectedWithdrawal.id || rejectionReason.trim().length < 10}
              >
                {actionLoading === selectedWithdrawal.id ? 'Rejecting...' : 'Confirm Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminWithdrawals;
